import {preflightZipArchive} from './fenzsafeArchivePreflight';

export type DocxTextFailure = 'archive_rejected' | 'parse_failed';

/** Safe, non-diagnostic failure classification for callers that must choose public copy. */
export class DocxTextError extends Error {
  constructor(readonly code: DocxTextFailure) {
    super(code);
    this.name = 'DocxTextError';
  }
}

let mammothModulePromise: Promise<typeof import('mammoth')> | null = null;

/** Load the already-pinned parser only when a preflighted DOCX actually needs text extraction. */
async function loadMammoth(): Promise<typeof import('mammoth')> {
  if (!mammothModulePromise) {
    mammothModulePromise = import('mammoth').catch((error: unknown) => {
      mammothModulePromise = null;
      throw error;
    });
  }
  return mammothModulePromise;
}

/**
 * Extracts plain text from a DOCX entirely in this browser tab.
 *
 * The ZIP central directory must pass the shared bomb/structure preflight before Mammoth sees
 * any bytes. `extractRawText()` has no HTML/image/relationship rendering surface: scripts,
 * macros, markup, embedded objects and external relationships are never executed or fetched.
 * Callers retain their own raw-byte and elapsed-time budgets because discovery and the stored
 * document viewer intentionally have different limits. A Promise timeout can suppress a late
 * result, but it cannot pre-empt Mammoth's synchronous main-thread work.
 */
export async function extractDocxRawText(buffer: ArrayBuffer): Promise<string> {
  if (!preflightZipArchive(buffer).ok) {
    throw new DocxTextError('archive_rejected');
  }

  try {
    const mammoth = await loadMammoth();
    const result = await mammoth.extractRawText({arrayBuffer: buffer});
    return result.value;
  } catch (error) {
    if (error instanceof DocxTextError) throw error;
    throw new DocxTextError('parse_failed');
  }
}
